import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storeService } from '../services/storeService';
import { StoreProductDetail, StoreVariant, StoreVariantColor } from '../models';
import { useCartStore } from '../store/cartStore';
import { Spinner } from '../components/ui/Spinner';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { formatCurrency } from '../utils/currency';
import { safeImageUrl } from '../utils/url';

const GENDER_LABELS: Record<number, string> = {
  1: 'Masculino',
  2: 'Feminino',
  3: 'Outros',
  4: 'Unisexo',
};

const AGE_VARIATION_TYPE = 0;

interface AgeSizeGuideRow {
  age: string;
  height: string;
  weight: string;
}

const AGE_SIZE_GUIDE: AgeSizeGuideRow[] = [
  { age: '2 anos', height: '85 - 92 cm', weight: '12 - 14 kg' },
  { age: '4 anos', height: '98 - 104 cm', weight: '15 - 17 kg' },
  { age: '6 anos', height: '110 - 116 cm', weight: '19 - 22 kg' },
  { age: '8 anos', height: '122 - 128 cm', weight: '24 - 28 kg' },
  { age: '10 anos', height: '134 - 140 cm', weight: '29 - 34 kg' },
  { age: '12 anos', height: '146 - 152 cm', weight: '36 - 42 kg' },
  { age: '14 anos', height: '158 - 164 cm', weight: '45 - 52 kg' },
];

function extractUniqueColors(variants: StoreVariant[]): StoreVariantColor[] {
  const colors: StoreVariantColor[] = [];
  const seen = new Set<number>();
  for (const v of variants) {
    for (const c of v.colors) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        colors.push(c);
      }
    }
  }
  return colors;
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addItem = useCartStore((s) => s.addItem);

  const [product, setProduct] = useState<StoreProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState('');
  const [selectedColor, setSelectedColor] = useState<StoreVariantColor | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const loadedIdRef = useRef<number | null>(null);

  const openLightbox = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel((z) => Math.min(z + 0.5, 4));
    setPanOffset({ x: 0, y: 0 });
  };

  const handleZoomOut = () => {
    setZoomLevel((z) => {
      const next = Math.max(z - 0.5, 1);
      if (next === 1) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  };

  const handleLightboxPrev = useCallback(() => {
    if (!product || product.images.length <= 1) return;
    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
    const prevIdx = currentIdx <= 0 ? product.images.length - 1 : currentIdx - 1;
    setMainImage(product.images[prevIdx].imageUrl);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, [product, mainImage]);

  const handleLightboxNext = useCallback(() => {
    if (!product || product.images.length <= 1) return;
    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
    const nextIdx = currentIdx >= product.images.length - 1 ? 0 : currentIdx + 1;
    setMainImage(product.images[nextIdx].imageUrl);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, [product, mainImage]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') handleLightboxPrev();
      if (e.key === 'ArrowRight') handleLightboxNext();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, handleLightboxPrev, handleLightboxNext]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (zoomLevel <= 1) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, offsetX: panOffset.x, offsetY: panOffset.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isPanning) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setPanOffset({ x: panStartRef.current.offsetX + dx, y: panStartRef.current.offsetY + dy });
  };

  const handlePointerUp = () => {
    setIsPanning(false);
  };

  useEffect(() => {
    if (!id) return;
    const numId = Number(id);
    
    // Evita dupla execução em StrictMode: se já carregou este ID, pula
    if (loadedIdRef.current === numId) return;
    
    loadedIdRef.current = numId;
    setLoading(true);
    storeService
      .getProduct(numId)
      .then((data) => {
        setProduct(data);
        setMainImage(data.images[0]?.imageUrl ?? '');

        const colors = extractUniqueColors(data.variants);
        if (colors.length > 0) {
          const colorWithStock = colors.find((c) =>
            data.variants.some((v) => v.stockQuantity > 0 && v.colors.some((vc) => vc.id === c.id))
          );
          setSelectedColor(colorWithStock ?? colors[0]);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (!product) return <div className="text-center py-20 text-text-light">Produto não encontrado.</div>;

  const hasVariants = product.variants.length > 0;
  const hasColors = product.variants.some((v) => v.colors.length > 0);
  const hasAgeVariation = product.variants.some((v) => v.variationType === AGE_VARIATION_TYPE);

  // Unique colors across all variants
  const uniqueColors: StoreVariantColor[] = hasColors ? extractUniqueColors(product.variants) : [];

  // Unique variation values available for the selected color (all colors if none selected yet)
  const uniqueValues: string[] = [];
  {
    const seen = new Set<string>();
    for (const v of product.variants) {
      if (selectedColor && !v.colors.some((c) => c.id === selectedColor.id)) continue;
      if (!seen.has(v.variationValue)) {
        seen.add(v.variationValue);
        uniqueValues.push(v.variationValue);
      }
    }
  }

  // Resolve the actual variant from selected color + value
  const resolvedVariant: StoreVariant | null = (() => {
    if (!hasVariants) return null;
    if (hasColors && selectedColor && selectedValue) {
      return product.variants.find(
        (v) => v.variationValue === selectedValue && v.colors.some((c) => c.id === selectedColor.id)
      ) ?? null;
    }
    if (!hasColors && selectedValue) {
      return product.variants.find((v) => v.variationValue === selectedValue) ?? null;
    }
    if (hasColors && !selectedColor && uniqueColors.length === 0 && selectedValue) {
      return product.variants.find((v) => v.variationValue === selectedValue) ?? null;
    }
    return null;
  })();

  const stockQuantity = resolvedVariant?.stockQuantity ?? 0;
  const isOutOfStock = hasVariants && resolvedVariant !== null && stockQuantity === 0;
  const canAddToCart = (!hasVariants || resolvedVariant !== null) && !isOutOfStock;

  const handleIncreaseQuantity = () => {
    if (quantity < stockQuantity) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    if (value >= 1 && value <= stockQuantity) {
      setQuantity(value);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    const result = addItem({
      productId: product.id,
      variantId: resolvedVariant?.id ?? 0,
      productName: product.name,
      variantLabel: resolvedVariant?.label ?? '',
      variantSku: resolvedVariant?.sku,
      price: product.price,
      promotionalPrice: product.promotionalPrice,
      imageUrl: mainImage,
      quantity: quantity,
      stockQuantity: resolvedVariant?.stockQuantity ?? 0,
    });
    if (result === 'max_stock') return;
    setQuantity(1);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const displayPrice = product.promotionalPrice ?? product.price;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate border border-champagne-dark hover:border-chocolate bg-white hover:bg-champagne px-4 py-2 rounded-full mb-6 transition-all duration-150"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Imagens */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-champagne-light group cursor-zoom-in min-w-0" onClick={openLightbox}>
            {mainImage ? (
              <img src={safeImageUrl(mainImage)} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-5xl">👗</div>
            )}
            <button
              aria-label="Ampliar imagem"
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
            {product.images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
                    const prevIdx = currentIdx <= 0 ? product.images.length - 1 : currentIdx - 1;
                    setMainImage(product.images[prevIdx].imageUrl);
                  }}
                  aria-label="Imagem anterior"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const currentIdx = product.images.findIndex((img) => img.imageUrl === mainImage);
                    const nextIdx = currentIdx >= product.images.length - 1 ? 0 : currentIdx + 1;
                    setMainImage(product.images[nextIdx].imageUrl);
                  }}
                  aria-label="Próxima imagem"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 shadow-md z-10"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {product.images.map((img) => (
                    <span
                      key={img.id}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        img.imageUrl === mainImage ? 'bg-chocolate' : 'bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img) => (
                <button
                  key={img.id}
                  onClick={() => setMainImage(img.imageUrl)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    mainImage === img.imageUrl ? 'border-chocolate' : 'border-transparent'
                  }`}
                >
                  <img src={safeImageUrl(img.imageUrl)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm text-text-light">
              {product.categoryName}
              {GENDER_LABELS[product.gender] && ` · ${GENDER_LABELS[product.gender]}`}
            </p>
            <h1 className="text-2xl font-bold text-text-dark mt-1">{product.name}</h1>
            {product.sku && <p className="text-xs text-text-light mt-1 font-mono">SKU: {product.sku}</p>}
          </div>

          <div className="flex items-baseline gap-2">
            {product.promotionalPrice && (
              <span className="text-text-light line-through text-sm">R$ {formatCurrency(product.price)}</span>
            )}
            <span className="text-2xl font-bold text-chocolate">R$ {formatCurrency(displayPrice)}</span>
          </div>

          {product.description && (
            <p className="text-text-medium text-base leading-relaxed">{product.description}</p>
          )}

          {/* Seleção de cor */}
          {uniqueColors.length > 0 && (
            <div>
              <p className="text-sm font-medium text-text-medium mb-2">
                Cor{selectedColor ? `: ${selectedColor.name}` : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {uniqueColors.map((color) => (
                  <button
                    key={color.id}
                    onClick={() => {
                      setSelectedColor(color);
                      setSelectedValue(null);
                    }}
                    title={color.name}
                    aria-label={color.name}
                    aria-pressed={selectedColor?.id === color.id}
                    className={`w-9 h-9 rounded-full border-2 transition-all ${
                      selectedColor?.id === color.id
                        ? 'border-chocolate scale-110 shadow-md'
                        : 'border-champagne-dark hover:border-chocolate/60 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.hexadecimal }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Seleção de variação (tamanho / idade / etc) */}
          {hasVariants && uniqueValues.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <p className="text-sm font-medium text-text-medium">Variação</p>
                {hasAgeVariation && (
                  <button
                    type="button"
                    onClick={() => setSizeGuideOpen(true)}
                    className="text-xs text-chocolate underline underline-offset-2 hover:text-chocolate/80 transition-colors"
                  >
                    Ver guia de tamanhos
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {uniqueValues.map((val) => {
                  const variant = product.variants.find(
                    (v) => v.variationValue === val && (!selectedColor || v.colors.some((c) => c.id === selectedColor.id))
                  );
                  const outOfStock = variant ? variant.stockQuantity === 0 : false;
                  return (
                    <button
                      key={val}
                      onClick={() => !outOfStock && setSelectedValue(val)}
                      disabled={outOfStock}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-colors relative ${
                        outOfStock
                          ? 'border-champagne-dark text-text-light opacity-50 cursor-not-allowed line-through'
                          : selectedValue === val
                          ? 'bg-chocolate text-white border-chocolate'
                          : 'border-champagne-dark text-text-medium hover:border-chocolate'
                      }`}
                    >
                      {variant?.label ?? val}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Seleção de quantidade */}
          {hasVariants ? (
            resolvedVariant && (
              <div>
                <p className="text-sm font-medium text-text-medium mb-2">Quantidade</p>
                <p className="text-xs text-text-light mb-2">Disponível: {stockQuantity}</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDecreaseQuantity}
                    disabled={quantity <= 1}
                    aria-label="Diminuir quantidade"
                    className="w-11 h-11 rounded-lg border border-champagne-dark hover:border-chocolate text-chocolate disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors text-lg"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={stockQuantity}
                    value={quantity}
                    onChange={handleQuantityChange}
                    className="w-16 h-11 px-2 text-center border border-champagne-dark rounded-lg text-text-dark focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate"
                  />
                  <button
                    onClick={handleIncreaseQuantity}
                    disabled={quantity >= stockQuantity}
                    aria-label="Aumentar quantidade"
                    className="w-11 h-11 rounded-lg border border-champagne-dark hover:border-chocolate text-chocolate disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            )
          ) : null}

          <Button
            fullWidth
            size="lg"
            variant="primary"
            onClick={handleAddToCart}
            disabled={!canAddToCart || addedToCart}
          >
            {isOutOfStock ? 'Esgotado' : addedToCart ? 'Adicionado ✓' : 'Adicionar ao carrinho'}
          </Button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && mainImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Toolbar */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <span className="text-white/60 text-sm mr-2">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
              disabled={zoomLevel <= 1}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
              disabled={zoomLevel >= 4}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
            <button
              onClick={closeLightbox}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Prev/Next */}
          {product.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleLightboxPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleLightboxNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] overflow-hidden select-none"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={safeImageUrl(mainImage)}
              alt={product.name}
              className="max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-200"
              style={{
                transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
              }}
              draggable={false}
            />
          </div>

          {/* Thumbnails */}
          {product.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {product.images.map((img) => (
                <button
                  key={img.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMainImage(img.imageUrl);
                    setZoomLevel(1);
                    setPanOffset({ x: 0, y: 0 });
                  }}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${
                    mainImage === img.imageUrl ? 'border-white' : 'border-white/30 hover:border-white/60'
                  }`}
                >
                  <img src={safeImageUrl(img.imageUrl)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} title="Guia de tamanhos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-champagne-dark">
                <th className="py-2 pr-3 font-medium text-text-medium">Idade</th>
                <th className="py-2 pr-3 font-medium text-text-medium">Altura aproximada</th>
                <th className="py-2 font-medium text-text-medium">Peso aproximado</th>
              </tr>
            </thead>
            <tbody>
              {AGE_SIZE_GUIDE.map((row) => (
                <tr key={row.age} className="border-b border-champagne">
                  <td className="py-2 pr-3 text-text-dark font-medium whitespace-nowrap">{row.age}</td>
                  <td className="py-2 pr-3 text-text-medium whitespace-nowrap">{row.height}</td>
                  <td className="py-2 text-text-medium whitespace-nowrap">{row.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-text-light mt-4">
          Medidas aproximadas — podem variar conforme o modelo da peça. Em caso de dúvida, fale com a vendedora.
        </p>
      </Modal>
    </div>
  );
}
